// src/lib/materialNaming.mjs
// ───────────────────────────────────────────────────────────────────────────
// 🏷️  Generador de nombre legible + código parseable del material (Slice 2)
//
// `displayName` y `specCode` se GENERAN (read-only) de los atributos estructurados
// por `specOrder` de la plantilla (src/lib/materialTemplates.mjs), MAYÚSCULAS. El
// `displayName` da la legibilidad que el `MAT-NNNNN` numérico pierde; el `specCode`
// es el string parseable/buscable y la CLAVE de deduplicación.
//
//   generateDisplayName('CORRUGATED', {flute:'B',test:'200#',outerLiner:'KRAFT',innerLiner:'KRAFT'},
//                       {dimensions:{width:72,height:100,unit:'in'}})
//     → "CORRUGATED · B · 200# · KRAFT/KRAFT · 72×100 IN"
//   generateSpecCode(... )  → "COR-B-200-KK-72x100"
//
// PURO y .mjs: lo consumen el cliente (MaterialModal, Slice 4), los unit tests y el
// script que arma el MAPEO de los 43. La Cloud Function (Slice 3) usará una copia
// server-side en functions/lib/ — `functions/` no puede importar `src/` en deploy
// (mismo patrón que production.js ↔ functions/lib/sheets.js). "Reusar, no duplicar"
// dentro de cada runtime.
// ───────────────────────────────────────────────────────────────────────────
import { getTemplate } from './materialTemplates.mjs';

const MM_PER_IN = 25.4;

// Agrupa los atributos de la plantilla por `specOrder` (preservando el orden de
// aparición) y, dentro de cada grupo, recoge los VALORES presentes (en orden).
// `requiredOnly`: solo atributos `required` (para la CLAVE de identidad — los
// cosméticos opcionales NO crean un SKU distinto, ver materialIdentityKey).
function valueGroups(category, attributes, requiredOnly = false) {
  const tpl = getTemplate(category);
  if (!tpl) return [];
  const order = [];
  const byOrder = new Map();
  for (const def of tpl.attributes) {
    // En la CLAVE de identidad entran los requeridos + SIEMPRE `brand` (distingue SKU:
    // ULTRABOARD ≠ DURAPRIME aunque brand sea opcional en RIGID); lo cosmético opcional
    // (caras/liners no-req, test) NO entra.
    if (requiredOnly && !def.required && def.field !== 'brand') continue;
    const v = attributes ? attributes[def.field] : undefined;
    if (v === undefined || v === null || String(v).trim() === '') continue;
    if (!byOrder.has(def.specOrder)) { byOrder.set(def.specOrder, []); order.push(def.specOrder); }
    byOrder.get(def.specOrder).push(String(v).trim());
  }
  return order.sort((a, b) => a - b).map((o) => byOrder.get(o));
}

const categoryDisplay = (category) => String(category || '').replace(/_/g, ' ').toUpperCase();
const dimsOf = (common) => (common && common.dimensions) || (common && common.dims) || null;

/** Nombre legible read-only (MAYÚSCULAS, separador ·, grupos del mismo specOrder con /). */
export function generateDisplayName(category, attributes, common = {}) {
  const parts = [categoryDisplay(category)];
  for (const g of valueGroups(category, attributes)) {
    parts.push(g.map((v) => v.toUpperCase()).join('/'));
  }
  // Grosor en la unidad nativa de la familia (cierra la colisión de specCode/displayName);
  // exento en corrugado/paperboard (la flauta/caliper ya lo codifican).
  const nt = nativeThickness(category, attributes, common);
  if (nt) parts.push(nt.display);
  const d = dimsOf(common);
  if (d && d.width != null && d.height != null) {
    parts.push(`${d.width}×${d.height} ${String(d.unit || 'in').toUpperCase()}`);
  }
  return parts.join(' · ');
}

// Token de specCode: grupo multi-valor → iniciales (KRAFT,KRAFT→KK); single → limpio
// (alfanumérico en MAYÚSCULAS: "200#"→"200", "RE-BOARD"→"REBOARD").
const cleanToken = (v) => String(v).toUpperCase().replace(/[^A-Z0-9]/g, '');
const initial = (v) => (cleanToken(v)[0] || '');

/** Código parseable con `specPrefix` (CLAVE de dedup). Ej.: COR-B-200-KK-72x100. */
export function generateSpecCode(category, attributes, common = {}) {
  const tpl = getTemplate(category);
  if (!tpl) return '';
  const segs = [tpl.specPrefix];
  for (const g of valueGroups(category, attributes)) {
    segs.push(g.length > 1 ? g.map(initial).join('') : cleanToken(g[0]));
  }
  const nt = nativeThickness(category, attributes, common);
  if (nt) segs.push(nt.code); // ej. "1/8" (rigid) · "16MM" (board) · "30MIL" (styrene)
  const d = dimsOf(common);
  if (d && d.width != null && d.height != null) {
    segs.push(`${cleanToken(d.width)}x${cleanToken(d.height)}`);
  }
  return segs.join('-');
}

/**
 * Canonicaliza el espesor en unidad NATIVA por familia (lo que captura el form)
 * a mm (storage canónico). Acepta { value, unit } con unit ∈ {mm,in,mil} o un número
 * pelado (se asume mm). Las fracciones de pulgada llegan como decimal in
 * (1/8" → { value:0.125, unit:'in' }); styrene en mil (.030" → 30 mil). null si vacío.
 */
export function canonicalThicknessMm(thickness) {
  if (thickness == null || thickness === '') return null;
  let value; let unit;
  if (typeof thickness === 'object') {
    value = Number(thickness.value);
    unit = String(thickness.unit || 'mm').toLowerCase();
  } else {
    value = Number(thickness);
    unit = 'mm';
  }
  if (!Number.isFinite(value)) return null;
  let mm;
  if (unit === 'in') mm = value * MM_PER_IN;
  else if (unit === 'mil') mm = (value * MM_PER_IN) / 1000;
  else mm = value;
  return Math.round(mm * 1000) / 1000;
}

/** Espesor canónico en mm (convierte in→mm; redondeo estable para comparar). */
export function thicknessMm(common = {}) {
  const t = common.thickness;
  if (t == null) return null;
  const value = typeof t === 'object' ? Number(t.value) : Number(t);
  if (!Number.isFinite(value)) return null;
  const unit = (typeof t === 'object' && t.unit) ? String(t.unit).toLowerCase() : 'mm';
  const mm = unit === 'in' ? value * MM_PER_IN : value;
  return Math.round(mm * 1000) / 1000;
}

// mm → fracción de pulgada reducida (denominador 32): 3.175→"1/8", 6.35→"1/4", 12.7→"1/2".
function mmToFraction(mm) {
  const inch = mm / MM_PER_IN;
  let num = Math.round(inch * 32);
  if (num <= 0) return null;
  let den = 32;
  while (num % 2 === 0 && den % 2 === 0) { num /= 2; den /= 2; }
  return den === 1 ? `${num}` : `${num}/${den}`;
}

/**
 * Grosor en la unidad NATIVA de la familia, para code+displayName (storage sigue en mm).
 * rigid=fracción (styrene=mil) · board/coroplast/wood=mm · corrugated/paperboard=EXENTO
 * (la flauta/caliper ya codifican el grosor → null). Devuelve `{display, code}` o null.
 */
export function nativeThickness(category, attributes, common = {}) {
  const tpl = getTemplate(category);
  const mm = thicknessMm(common);
  if (!tpl || mm == null) return null;
  const mode = tpl.thicknessDisplay || 'none';
  if (mode === 'none') return null;
  if (category === 'RIGID' && attributes && attributes.subtype === 'STYRENE') {
    const mil = Math.round((mm / MM_PER_IN) * 1000); // gauge-mil (0.762 mm = 30 mil)
    return { display: `${mil} MIL`, code: `${mil}MIL` };
  }
  if (mode === 'fraction') {
    const f = mmToFraction(mm);
    return f ? { display: `${f}"`, code: f } : null;
  }
  if (mode === 'mm') {
    const v = Math.round(mm * 100) / 100;
    return { display: `${v} MM`, code: `${v}MM` };
  }
  return null;
}

/**
 * CLAVE de identidad para deduplicar. = `specPrefix` + atributos **requeridos**
 * (por specOrder) + dimensiones + **espesor**. Reglas (de los casos de Daniel):
 *   - El espesor ENTRA: REBOARD 63/90 16 mm vs 10 mm → claves distintas (NO dup).
 *   - Los atributos OPCIONALES (caras/liners no-requeridos, test, brand opcional)
 *     NO entran: una "FALCONBOARD " sin caras declaradas dedupea con "FALCONBOARD
 *     K/K" del mismo grosor/medida (sí dup) — lo cosmético no crea un SKU nuevo.
 *   - Case/espacios normalizados: variantes sucias del nombre, una vez parseadas a
 *     atributos canónicos, colapsan a la misma clave.
 * (El `specCode` completo —con opcionales— queda para display/búsqueda, no para identidad.)
 */
export function materialIdentityKey(category, attributes, common = {}) {
  const tpl = getTemplate(category);
  const segs = [tpl ? tpl.specPrefix : 'UNMAPPED'];
  for (const g of valueGroups(category, attributes, true)) {
    segs.push(g.length > 1 ? g.map(initial).join('') : cleanToken(g[0]));
  }
  const d = dimsOf(common);
  if (d && d.width != null && d.height != null) segs.push(`${cleanToken(d.width)}x${cleanToken(d.height)}`);
  const th = thicknessMm(common);
  return `${segs.join('-').toUpperCase().replace(/\s+/g, '')}#${th == null ? '' : th}`;
}

/** Busca un duplicado de `candidate` entre `existing` (misma clave de identidad). */
export function findDuplicateMaterial(existing, candidate) {
  const key = materialIdentityKey(candidate.category, candidate.attributes, candidate);
  return (existing || []).find((m) => {
    if (candidate.id && m.id === candidate.id) return false;
    return materialIdentityKey(m.category, m.attributes, m) === key;
  }) || null;
}

/** Agrupa una lista por clave de identidad y devuelve SOLO los clústeres duplicados (>1). */
export function findDuplicateClusters(materials) {
  const byKey = new Map();
  for (const m of materials || []) {
    const key = materialIdentityKey(m.category, m.attributes, m);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(m);
  }
  return Array.from(byKey.entries())
    .filter(([, arr]) => arr.length > 1)
    .map(([key, arr]) => ({ key, members: arr }));
}
